// /quotes/intake/:id, a quote document (Phase 9, plan 9C; mock M7,
// docs/plans/lens-dealer-portal/quote-documents.html): the PDF on the left, and on the right
// what Omni read from it while it reads, why it couldn't, the review a person checks (the
// customer, contacts, term, each line against the dealer's catalog, the totals) before
// Confirm creates the prospect, contacts and draft, or where it went once confirmed.
// Nothing is created until Confirm.
import React, { useCallback, useEffect, useState } from "react";
import {
  formatCents,
  formatTerm,
  inlineFixes,
  quotableBlockers,
  roleLabel,
  type CatalogProductV2,
  type CatalogSyncState,
  type ContractTerm,
  type CustomerContact,
  type CustomerSummary,
  type CustomerV2,
  type Dealer,
  type IntakeContact,
  type IntakeLine,
  type IntakePart,
  type PricedProduct,
  type ProductInput,
  type QuotableBlocker,
  type Quote,
  type QuoteIntakeView,
  type QuoteIntakeLineView,
  type SyncStatus,
} from "@phoneware/lens-domain";
import { ApiError } from "../api.js";
import {
  alreadyContact,
  belowFloor,
  billWord,
  billingDiffers,
  codeLine,
  confirmRequest,
  contactName,
  draftLines,
  fileSize,
  gate,
  intakeView,
  lineState,
  lineText,
  matchedWhy,
  onebillLacksCode,
  onebillWithCode,
  openWords,
  partChip,
  printedBilling,
  sourceLabel,
  rememberConfirmation,
  sharedWords,
  startReview,
  totalsCheck,
  type CodeWordsContext,
  type ContactEdit,
  type GateContext,
  type Review,
} from "../api-intakes.js";
import { pricedForDealer } from "../api-templates.js";
import { AddSheet, type AddStart } from "./catalog/AddSheet.js";
import { OneBillPicker } from "./catalog/parts.js";
import { atRef, billedText, planText, withoutSuffix } from "./catalog/model.js";
import { Icon } from "../icons.js";
import { SourcePane } from "../source-pane.js";
import type { PdfHighlight } from "../pdf-view.js";
import { Link, navigate } from "../router.js";
import { isNotYours, useLoad, useSession } from "../session.js";
import { TERMS, errorText, plural, when, whenIn } from "../format.js";
import { Callout, Chip, Crumb, Head, NotYours, Sheet, ViewOnly, useTitle, useToast } from "../ui.js";
import { useRepNames } from "./Quotes.js";
import { FileIcon, UploadSheet, ViaText } from "./intake-bits.js";

/** How often a page that is still reading asks the server again. */
const POLL_MS = 2500;

const MATCHED_BY: Record<"phone" | "name" | "company_key" | "autotask_id", string> = { phone: "phone", name: "name", company_key: "company name", autotask_id: "the same Autotask company" };

type Name = (email: string) => string;

function IntakeHead({ intake, title, name, actions }: { intake: QuoteIntakeView; title: string; name: Name; actions?: React.ReactNode }): React.ReactElement {
  return (
    <>
      <Crumb to="/quotes">Quotes</Crumb>
      <div className="head">
        <div>
          <h1 data-testid="intake-title">{title}</h1>
          <p className="sub byline">
            <span>{sourceLabel(intake.source)}</span>
            <span>· {intake.number}</span>
            <span>·</span>
            <ViaText intake={intake} name={name} chip />
            <span>· {when(intake.createdAt)}</span>
          </p>
        </div>
        {actions ? <div className="actions">{actions}</div> : null}
      </div>
    </>
  );
}

function DiscardButton({ onClick }: { onClick: () => void }): React.ReactElement {
  return (
    <button className="btn" type="button" data-testid="intake-discard" onClick={onClick}>
      <Icon name="trash-2" size={14} />
      Discard
    </button>
  );
}

function Split({
  intake,
  mode,
  highlight,
  mark,
  part,
  onPart,
  onPages,
  staff,
  children,
}: {
  intake: QuoteIntakeView;
  mode: "reading" | "review" | "fail" | "done";
  highlight?: PdfHighlight | null;
  mark?: string | null;
  part?: number;
  onPart?: (index: number) => void;
  onPages?: (n: number) => void;
  staff?: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [localPart, setLocalPart] = useState(0);
  const seq = highlight?.seq;
  useEffect(() => {
    if (seq != null) setOpen(true);
  }, [seq]);
  return (
    <div className="ix">
      <SourcePane
        intake={intake}
        mode={mode}
        part={part ?? localPart}
        onPart={onPart ?? setLocalPart}
        highlight={highlight ?? null}
        mark={mark ?? null}
        open={open}
        onToggle={() => setOpen((o) => !o)}
        onPages={onPages}
        staff={staff ?? false}
      />
      <div className="ix-main">{children}</div>
    </div>
  );
}

export function QuoteIntakePage({ id }: { id: string }): React.ReactElement {
  const { me, api, everyDealer, canWrite } = useSession();
  const toast = useToast();
  const name = useRepNames();
  const ops = everyDealer;
  const loaded = useLoad<QuoteIntakeView>(() => api.quoteIntake(id), [id]);
  const intake = loaded.data;
  const view = intake ? intakeView(intake) : null;
  const dealers = useLoad<Dealer[]>(() => (ops ? api.dealers() : Promise.resolve([])), [ops]);
  const dealer = ops ? (dealers.data?.find((d) => d.id === intake?.dealerId) ?? null) : me.dealer;
  const [sheet, setSheet] = useState<"discard" | "upload" | null>(null);
  const [pages, setPages] = useState<number | null>(null);
  const onPages = useCallback((n: number) => setPages(n), []);
  useTitle(intake ? intake.number : "Quote document");

  // Reading happens on the server; ask again until it is done.
  useEffect(() => {
    if (view !== "extracting") return;
    const timer = window.setTimeout(() => loaded.reload(), POLL_MS);
    return () => window.clearTimeout(timer);
    // `loaded` is a fresh object every render; the intake changing is what re-arms the timer.
  }, [intake, view]);

  if (loaded.error && !intake) {
    if (isNotYours(loaded.error)) return <NotYours kind="quote document" back="/quotes" />;
    return (
      <>
        <Crumb to="/quotes">Quotes</Crumb>
        <Head title="Quote document" />
        <Callout kind="stop" icon="triangle-alert">
          {loaded.error.message}
        </Callout>
      </>
    );
  }
  if (!intake || !view) {
    return (
      <>
        <Crumb to="/quotes">Quotes</Crumb>
        <Head title="Quote document" />
        <section className="card">
          <div className="empty">Loading the quote document.</div>
        </section>
      </>
    );
  }

  const dealerName = dealer?.name ?? "the dealer";
  const discard = () => setSheet("discard");
  const sheets = (
    <>
      {sheet === "discard" ? (
        <DiscardSheet
          intake={intake}
          name={name}
          dealerName={dealerName}
          onClose={() => setSheet(null)}
          onDone={() => {
            setSheet(null);
            navigate("/quotes");
            toast(`Discarded ${intake.number}. Nothing was created from it.`);
          }}
        />
      ) : null}
      {sheet === "upload" ? <UploadSheet onClose={() => setSheet(null)} /> : null}
    </>
  );

  if (view === "extracting") {
    return (
      <div className="ix-page">
        <IntakeHead intake={intake} title="Reading the quote document" name={name} actions={<DiscardButton onClick={discard} />} />
        <Split intake={intake} mode="reading" onPages={onPages}>
          <section className="card" data-testid="intake-reading">
            <div className="card-h">
              <h2>Omni is reading it</h2>
            </div>
            <ul className="steps">
              <li className="done">
                <Icon name="circle-check" size={16} />
                <span>
                  Uploaded
                  <small>{[fileSize(intake.source.bytes), pages != null ? plural(pages, "page") : null].filter(Boolean).join(", ")}</small>
                </span>
              </li>
              <li className="now">
                <Icon name="loader-circle" size={16} className="spin" />
                <span>
                  Reading the customer, contacts, term and lines
                  <small>Quantities and prices are kept exactly as printed.</small>
                </span>
              </li>
              <li className="todo">
                <Icon name="circle" size={16} />
                <span>
                  Matching them to {dealerName}&rsquo;s customers and catalog
                  <small>By code, then by name. Anything without a sure match is left for you.</small>
                </span>
              </li>
              <li className="todo">
                <Icon name="circle" size={16} />
                <span>Ready for you to check</span>
              </li>
            </ul>
            <p className="card-note">You can leave this page. The reading carries on and waits under Quote documents in Quotes.</p>
          </section>
          <div className="callout">
            <Icon name="info" size={15} />
            <div className="cbody">Nothing is created until you check it and confirm: no customer, no contact, no quote.</div>
          </div>
        </Split>
        {sheets}
      </div>
    );
  }

  if (view === "failed" || view === "nolines") {
    const unread = view === "failed";
    const tries = intake.attempts === 2 ? "twice" : intake.attempts > 2 ? `${intake.attempts} times` : "once";
    const onPagesText = pages == null ? "on it" : pages === 1 ? "on this page" : `on these ${plural(pages, "page")}`;
    const buttons = (
      <div className="btnrow">
        <button className="btn primary" type="button" data-testid="intake-try-again" onClick={() => setSheet("upload")}>
          <Icon name="refresh-cw" size={14} />
          Try again
        </button>
        <DiscardButton onClick={discard} />
      </div>
    );
    return (
      <div className="ix-page">
        <IntakeHead intake={intake} title={unread ? "Couldn\u2019t read this document" : "No lines found in this document"} name={name} />
        <Split intake={intake} mode="fail" onPages={onPages}>
          <section className="card">
            {unread ? (
              <div className="fail-card" data-testid="intake-failed">
                <span className="mark stop">
                  <Icon name="file-x" size={20} />
                </span>
                <h2>Omni couldn&rsquo;t read it</h2>
                <p>
                  It tried {tries} and couldn&rsquo;t make out the text {onPagesText}. Nothing was created.
                </p>
                <p>Try the file your quoting tool saved, or a sharper scan.</p>
                {buttons}
              </div>
            ) : (
              <div className="fail-card" data-testid="intake-nolines">
                <span className="mark warn">
                  <Icon name="file-search" size={20} />
                </span>
                <h2>No lines found</h2>
                <p>
                  Omni read {pages != null ? plural(pages, "page") : "it"} and found no products, quantities or prices {pages === 1 || pages == null ? "on it" : "on them"}. Nothing
                  was created.
                </p>
                <p>If the quote is in a separate file, upload that one.</p>
                {buttons}
              </div>
            )}
          </section>
        </Split>
        {sheets}
      </div>
    );
  }

  if (view === "confirmed") return <ConfirmedPage intake={intake} name={name} />;

  if (view === "discarded") {
    return (
      <div className="ix-page">
        <IntakeHead intake={intake} title="Discarded" name={name} />
        <section className="card" data-testid="intake-discarded">
          <div className="empty">This quote document was discarded. Nothing was created from it.</div>
        </section>
      </div>
    );
  }

  return (
    <div className="ix-page">
      <ReviewScreen
        key={intake.id}
        intake={intake}
        dealer={dealer}
        dealerName={dealerName}
        name={name}
        writes={canWrite("quotes.write", dealer)}
        onDiscard={discard}
        onReload={() => loaded.reload()}
        pages={pages}
        onPages={onPages}
      />
      {sheets}
    </div>
  );
}

function ConfirmedPage({ intake, name }: { intake: QuoteIntakeView; name: Name }): React.ReactElement {
  const { api } = useSession();
  const quoteId = intake.confirmed?.quoteId ?? null;
  const quote = useLoad<Quote | null>(() => (quoteId ? api.quote(quoteId) : Promise.resolve(null)), [quoteId]);
  const number = quote.data?.number ?? quoteId ?? "";
  return (
    <div className="ix-page">
      <IntakeHead intake={intake} title={`Confirmed into ${number}`} name={name} />
      <Split intake={intake} mode="done">
        <div className="callout ok" data-testid="intake-confirmed">
          <Icon name="circle-check" size={15} />
          <div className="cbody">
            <b>Confirmed into {number}</b>
            {intake.confirmed ? `${name(intake.confirmed.by)} confirmed it ${whenIn(intake.confirmed.at)}. ` : ""}The file stays with the quote.
            {quoteId ? (
              <div className="btnrow">
                <Link className="btn small white" to={`/quotes/${encodeURIComponent(quoteId)}`} data-testid="intake-open-quote">
                  Open {number}
                </Link>
              </div>
            ) : null}
          </div>
        </div>
      </Split>
    </div>
  );
}

function DiscardSheet({
  intake,
  name,
  dealerName,
  onClose,
  onDone,
}: {
  intake: QuoteIntakeView;
  name: Name;
  dealerName: string;
  onClose: () => void;
  onDone: () => void;
}): React.ReactElement {
  const { api } = useSession();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  async function discard(): Promise<void> {
    setBusy(true);
    setProblem(null);
    try {
      await api.discardQuoteIntake(intake.id);
      onDone();
    } catch (err) {
      setProblem(errorText(err));
      setBusy(false);
    }
  }
  return (
    <Sheet
      title={`Discard ${intake.number}?`}
      onClose={onClose}
      foot={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn stopfill" type="button" data-testid="intake-discard-yes" disabled={busy} onClick={() => void discard()}>
            <Icon name="trash-2" size={14} />
            Discard
          </button>
        </>
      }
    >
      <div className="filecard" data-testid="intake-discard-sheet">
        <FileIcon name={sourceLabel(intake.source)} />
        <span className="fn">
          <b>{sourceLabel(intake.source)}</b>
          <span>
            <ViaText intake={intake} name={name} /> · {when(intake.createdAt)}
          </span>
        </span>
      </div>
      <p className="upintro spaced">
        It comes off Quote documents for everyone at {dealerName}. No customer, contact or quote was created from it, so there is nothing else to undo.
      </p>
      {problem ? (
        <Callout kind="stop" icon="triangle-alert">
          {problem}
        </Callout>
      ) : null}
    </Sheet>
  );
}

// ------------------------------------------------------------------ the review

function ReviewScreen({
  intake,
  dealer,
  dealerName,
  name,
  writes,
  onDiscard,
  onReload,
  pages,
  onPages,
}: {
  intake: QuoteIntakeView;
  dealer: Dealer | null;
  dealerName: string;
  name: Name;
  /** False for a Phoneware tier that reads this dealer's documents without confirming them (mock M1). */
  writes: boolean;
  onDiscard: () => void;
  onReload: () => void;
  pages: number | null;
  onPages: (n: number) => void;
}): React.ReactElement {
  const { me, api, can, writeScope } = useSession();
  const toast = useToast();
  const ops = can("catalog.read_all");
  const canLink = can("catalog.link");
  const canOn = can("catalog.manage");
  const full = useLoad<CatalogProductV2[] | null>(() => (ops ? api.fullCatalog() : Promise.resolve(null)), [ops]);
  // Staff: OneBill's side of a code, from the last catalog check (#2057).
  const readsChecks = ops && can("all_dealers.read");
  const sync = useLoad<SyncStatus | null>(() => (readsChecks ? api.syncStatus() : Promise.resolve(null)), [readsChecks]);
  const dealerCatalog = useLoad<PricedProduct[] | null>(() => (ops ? Promise.resolve(null) : api.pricedCatalog()), [ops]);
  const customers = useLoad<CustomerSummary[]>(() => api.customers(), []);
  const [review, setReview] = useState<Review>(() => startReview(intake));
  const [saved, setSaved] = useState<Record<string, CatalogProductV2>>({});
  const [switched, setSwitched] = useState<ReadonlySet<string>>(() => new Set());
  const [fixingId, setFixingId] = useState<string | null>(null);
  const [fixError, setFixError] = useState<string | null>(null);
  /** The product being linked to OneBill: a line's own, or the one picked for it. */
  const [obFor, setObFor] = useState<string | null>(null);
  const chosenId = review.customer.kind === "existing" ? review.customer.id : null;
  const chosen = useLoad<CustomerV2 | null>(() => (chosenId ? api.customer(chosenId) : Promise.resolve(null)), [chosenId]);
  const [hl, setHl] = useState<{ index: number; seq: number } | null>(null);
  const [part, setPart] = useState(0);
  const [pick, setPick] = useState<number | null>(null);
  /** The line a Super admin is creating a product for. */
  const [adding, setAdding] = useState<number | null>(null);
  const [addBusy, setAddBusy] = useState(false);
  const [addProblem, setAddProblem] = useState<string | null>(null);
  const [custSheet, setCustSheet] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [draftContact, setDraftContact] = useState<ContactEdit>({ firstName: "", lastName: "", email: "", phone: "", title: "" });
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const x = intake.extraction?.result;
  const match = intake.match;
  if (!x || !match) {
    return (
      <Callout kind="stop" icon="triangle-alert">
        This document is marked ready, but Omni has no reading of it to show.
      </Callout>
    );
  }
  // Staff: the whole catalog as last saved here, with any product created on this page.
  const records = full.data
    ? [...full.data.map((p) => saved[p.id] ?? p), ...Object.values(saved).filter((p) => !full.data!.some((f) => f.id === p.id))]
    : null;
  const products = ops && dealer && records ? pricedForDealer(records, dealer) : (dealerCatalog.data ?? []);
  const catalogReady = ops ? Boolean(full.data && dealer) : dealerCatalog.data != null;
  const catalogError = ops ? full.error : dealerCatalog.error;
  const recordOf = (id: string): CatalogProductV2 | null => saved[id] ?? full.data?.find((p) => p.id === id) ?? null;
  const product = (pid: string | null): PricedProduct | null => products.find((p) => p.id === pid) ?? null;
  const isQuotable = (id: string): boolean => {
    const priced = products.find((p) => p.id === id);
    if (priced) return priced.quotable;
    const rec = recordOf(id);
    return Boolean(rec?.enabled && rec.billing && rec.billingLink?.state === "linked" && rec.onebill);
  };
  const blockersOf = (id: string): QuotableBlocker[] => {
    const rec = recordOf(id);
    return rec && dealer ? quotableBlockers(rec, dealer) : [];
  };
  const summaryOf = (cid: number): CustomerSummary | null => customers.data?.find((c) => c.autotaskCompanyId === cid) ?? null;
  const chosenCustomer: CustomerSummary | null = chosenId ? (chosen.data?.autotaskCompanyId === chosenId ? chosen.data : summaryOf(chosenId)) : null;
  const customerName =
    review.customer.kind === "existing" ? (chosenCustomer?.name ?? (match.customer.kind === "existing" ? match.customer.name : "")) : review.form.name.trim() || "the new prospect";
  const existingContacts = chosenId && chosen.data?.autotaskCompanyId === chosenId ? chosen.data.contacts : null;
  const ctx: GateContext = { catalog: products, existingContacts, customerName, dealerName, quotable: isQuotable };
  const g = !catalogReady
    ? { ok: false, msg: catalogError ? `${dealerName}'s catalog didn't load: ${catalogError.message}` : `Loading ${dealerName}'s catalog.` }
    : chosenId && !existingContacts
      ? { ok: false, msg: chosen.error ? `The customer didn't load: ${chosen.error.message}` : "Loading the customer." }
      : gate(intake, review, ctx);
  const set = (patch: Partial<Review>) => {
    setReview((r) => ({ ...r, ...patch }));
    setProblem(null);
  };
  // From the newest review: a product created or switched on after a wait mustn't undo a choice made meanwhile.
  const setLine = (i: number, choice: { pick: string | null; out: boolean }) => {
    setReview((r) => ({ ...r, lines: r.lines.map((c, k) => (k === i ? choice : c)) }));
    setProblem(null);
  };

  async function linkProduct(productId: string, productCode: string, pricePlanId: string): Promise<void> {
    setFixingId(productId);
    setFixError(null);
    try {
      const next = await api.setBillingLink(productId, { state: "linked", onebill: { productCode, pricePlanId } });
      setSaved((cur) => ({ ...cur, [next.id]: next }));
      setObFor(null);
      toast(`Linked to OneBill ${productCode}, plan ${pricePlanId}.`);
      onReload();
    } catch (err) {
      setFixError(errorText(err));
    } finally {
      setFixingId(null);
    }
  }

  async function switchOn(index: number, productId: string): Promise<void> {
    const rec = recordOf(productId);
    if (!rec) return;
    setFixingId(productId);
    setFixError(null);
    try {
      const next = await api.patchCatalog(productId, { enabled: true, expectedUpdatedAt: rec.updatedAt });
      setSaved((cur) => ({ ...cur, [next.id]: next }));
      setSwitched((cur) => new Set(cur).add(productId));
      setLine(index, { pick: productId, out: false });
      toast(`${next.name} is switched on.`);
      onReload();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        onReload();
        setFixError("It changed since this page loaded. Reloaded the product.");
      } else setFixError(errorText(err));
    } finally {
      setFixingId(null);
    }
  }

  /** Creates the product in Omni and Autotask, as the catalog's Add product does, and picks it for the line. */
  async function createFor(index: number, input: ProductInput): Promise<boolean> {
    setAddBusy(true);
    setAddProblem(null);
    try {
      const made = await api.createProduct(input);
      setSaved((cur) => ({ ...cur, [made.id]: made }));
      setLine(index, { pick: made.id, out: false });
      toast(`Created ${made.name} as ${atRef(made)} and read it back.`);
      return true;
    } catch (err) {
      const kept = err instanceof ApiError && err.status === 502 && err.body && typeof err.body === "object" && "product" in err.body ? (err.body.product as CatalogProductV2) : null;
      if (kept) {
        setSaved((cur) => ({ ...cur, [kept.id]: kept }));
        setLine(index, { pick: kept.id, out: false });
        toast(`Autotask didn\u2019t take ${kept.name}. Omni kept it; Retry from the catalog when Autotask is back.`);
        return true;
      }
      setAddProblem(errorText(err));
      return false;
    } finally {
      setAddBusy(false);
    }
  }

  const total = match.lines.length;
  const matched = match.lines.filter((m) => m.state === "matched").length;
  const summary =
    `${pages != null && intake.parts?.some((p) => p.kind === "pdf") ? `Omni read ${plural(pages, "page")} and found` : "Omni found"} ${plural(total, "line")}: ${matched} matched to ${dealerName}\u2019s catalog${total - matched ? `, ${total - matched} not` : ""}. ` +
    (match.customer.kind === "existing"
      ? `It matched the customer by ${MATCHED_BY[match.customer.why]}.`
      : `No ${dealerName} customer matched, so it filled in a new prospect from the quote.`);

  const obProduct = obFor ? recordOf(obFor) : null;
  const obLacks = obProduct ? onebillLacksCode(obProduct.id, sync.data?.catalog?.codes) : null;
  async function confirm(): Promise<void> {
    if (!g.ok || !x) return;
    setBusy(true);
    setProblem(null);
    const request = confirmRequest(intake, review, ctx);
    const lines = draftLines(intake, review, products);
    const adds = x.contacts.filter((c, i) => review.checked[i] && !alreadyContact(c, review, ctx.existingContacts));
    try {
      const result = await api.confirmQuoteIntake(intake.id, request);
      const madeEmails = new Set(result.contacts.map((c) => c.email?.toLowerCase()).filter(Boolean));
      const needsApproval = result.quote.status === "needs_approval";
      rememberConfirmation(result.quote.id, {
        intakeId: intake.id,
        intakeNumber: intake.number,
        filename: sourceLabel(intake.source),
        dealerName,
        newCustomer: request.customer.kind === "new",
        customerName: result.customer.name,
        accountNumber: result.customer.accountNumber ?? null,
        added: adds.filter((c) => c.email && madeEmails.has(c.email.trim().toLowerCase())).map(contactName),
        needsApproval,
        belowFloor: belowFloor(lines).length,
        omitted: [],
        signer: result.quote.contactName,
      });
      toast(needsApproval ? `${result.quote.number} created. Phoneware approves it before it goes out.` : `Draft ${result.quote.number} created`);
      navigate(`/quotes/${encodeURIComponent(result.quote.id)}`);
    } catch (err) {
      setProblem(errorText(err));
      setBusy(false);
    }
  }

  const confirmButton = (testId: string) => (
    <button className="btn primary" type="button" data-testid={testId} disabled={!g.ok || busy} title={g.ok ? undefined : g.msg} onClick={() => void confirm()}>
      {busy ? (
        <>
          <Icon name="loader-circle" size={15} className="spin" />
          Creating the draft
        </>
      ) : (
        <>
          <Icon name="check" size={15} />
          Confirm
        </>
      )}
    </button>
  );

  const lit = hl ? x.lines[hl.index] : null;
  const litPart = lit ? (intake.parts ?? []).find((p) => p.index === lit.part - 1) ?? (intake.parts ?? []).find((p) => p.index === lit.part) : null;
  const highlight: PdfHighlight | null = hl && lit ? { page: lit.page, keys: [lit.description, lit.code ?? "", lit.text], seq: hl.seq } : null;
  const mark = lit ? lineText(lit) : null;
  const low = belowFloor(draftLines(intake, review, products));

  return (
    <>
      <IntakeHead
        intake={intake}
        title="Check the quote document"
        name={name}
        actions={
          writes ? (
            <>
              <DiscardButton onClick={onDiscard} />
              {confirmButton("intake-confirm-top")}
            </>
          ) : null
        }
      />
      {writes ? null : (
        <ViewOnly testId="intake-view-only">
          {writeScope("quotes.write") === "house"
            ? `This is a ${dealerName} quote document. Sales quotes for Phoneware\u2019s own customers.`
            : `${roleLabel(me.user.role)} sees every dealer\u2019s quote documents and changes none of them.`}
        </ViewOnly>
      )}
      <Split intake={intake} mode="review" highlight={highlight} mark={mark} part={hl && litPart ? litPart.index : part} onPart={setPart} onPages={onPages} staff={ops}>
        <div className="callout" data-testid="intake-summary">
          <Icon name="scan-text" size={15} />
          <div className="cbody">{summary} Nothing is created until you confirm.</div>
        </div>

        {review.customer.kind === "existing" ? (
          <ExistingCustomerCard
            customer={chosenCustomer}
            why={review.customer.why}
            printed={x.customer}
            dealerName={dealerName}
            canMakeNew={match.customer.kind === "new"}
            onChange={() => setCustSheet(true)}
            onNew={() => set({ customer: { kind: "new" } })}
          />
        ) : (
          <NewCustomerCard
            review={review}
            candidates={match.customer.kind === "new" ? match.customer.candidates : []}
            printedName={x.customer.name ?? ""}
            summaryOf={summaryOf}
            dealerName={dealerName}
            onForm={(form) => set({ form })}
            onUse={(cid) => set({ customer: { kind: "existing", id: cid, why: "candidate" } })}
            onPick={() => setCustSheet(true)}
          />
        )}

        <section className="card" data-testid="intake-contacts">
          <div className="card-h">
            <h2>Contacts</h2>
            <span className="note">From the quote. The primary contact signs the QuoteLink.</span>
          </div>
          {x.contacts.length === 0 ? <div className="empty">The quote names no contacts.</div> : <p className="ct-g">On the quote</p>}
          {x.contacts.map((c, i) => {
            const edit = review.edits[i] ?? { firstName: c.firstName ?? "", lastName: c.lastName ?? "", email: c.email ?? "", phone: c.phone ?? "", title: c.title ?? "" };
            const exists = alreadyContact(edit, review, existingContacts);
            const here = existingContacts?.find((person) => edit.email.trim() && person.email?.trim().toLowerCase() === edit.email.trim().toLowerCase()) ?? null;
            const on = exists || Boolean(review.checked[i]);
            const state = exists ? `Already a contact at ${customerName}.` : review.checked[i] ? "Added as a contact when you confirm." : "Not added.";
            if (editing === `x${i}`) {
              return (
                <div className="ct" key={i} data-testid={`intake-contact-${i}`}>
                  <span />
                  <ContactForm
                    draft={draftContact}
                    onChange={setDraftContact}
                    saveLabel="Done"
                    onCancel={() => setEditing(null)}
                    onSave={() => {
                      set({ edits: review.edits.map((row, k) => (k === i ? draftContact : row)) });
                      setEditing(null);
                    }}
                  />
                </div>
              );
            }
            return (
              <div className={on ? "ct" : "ct off"} key={i} data-testid={`intake-contact-${i}`}>
                {exists ? (
                  <span className="have" title="Already a contact">
                    <Icon name="circle-check" size={16} />
                  </span>
                ) : (
                  <input
                    type="checkbox"
                    checked={Boolean(review.checked[i])}
                    aria-label={`Add ${contactName(edit)} as a contact`}
                    data-testid={`intake-contact-add-${i}`}
                    onChange={(e) => {
                      const checked = review.checked.map((v, k) => (k === i ? e.target.checked : v));
                      const primary = !e.target.checked && review.primary === i ? x.contacts.findIndex((_, k) => checked[k]) : review.primary;
                      set({ checked, primary });
                    }}
                  />
                )}
                <div className="cn">
                  <b>{contactName(edit)}</b> {edit.title ? <span className="role">{edit.title}</span> : null}
                  {c.role === "primary" || c.role === "billing" ? <span className="chip quiet">{c.role === "primary" ? "Primary" : "Billing"} on the quote</span> : null}
                  <div className="meta">
                    <span>
                      <Icon name="mail" size={12} />
                      {edit.email || "No email"}
                    </span>
                    {edit.phone ? (
                      <span>
                        <Icon name="phone" size={12} />
                        {edit.phone}
                      </span>
                    ) : null}
                  </div>
                  <span className="state">{state}</span>
                  {writes && !exists ? (
                    <div className="acts">
                      <button className="link" type="button" data-testid={`intake-contact-edit-${i}`} onClick={() => { setDraftContact(edit); setEditing(`x${i}`); }}>
                        <Icon name="pen-line" size={12} /> Edit
                      </button>
                    </div>
                  ) : null}
                </div>
                {here ? (
                  <label className="pri">
                    <input type="radio" name={`primary-${intake.id}`} checked={review.existingPrimaryId === here.autotaskContactId} data-testid={`intake-existing-primary-${i}`} onChange={() => set({ existingPrimaryId: here.autotaskContactId })} />
                    Primary
                  </label>
                ) : (
                  <label className="pri">
                    <input type="radio" name={`primary-${intake.id}`} checked={review.existingPrimaryId == null && review.primary === i} disabled={!on} data-testid={`intake-contact-primary-${i}`} onChange={() => set({ primary: i, existingPrimaryId: null })} />
                    Primary
                  </label>
                )}
              </div>
            );
          })}
          {writes && editing !== "new" ? (
            <div className="addct">
              <button className="btn small" type="button" data-testid="intake-add-contact" onClick={() => { setDraftContact({ firstName: "", lastName: "", email: "", phone: "", title: "" }); setEditing("new"); }}>
                <Icon name="user-plus" size={13} />Add a contact
              </button>
            </div>
          ) : null}
          {editing === "new" ? (
            <div className="ct" data-testid="intake-contact-new">
              <span />
              <ContactForm
                draft={draftContact}
                onChange={setDraftContact}
                saveLabel="Add"
                onCancel={() => setEditing(null)}
                onSave={() => {
                  set({ added: [...review.added, { ...draftContact, key: `a${review.added.length}`, include: true }] });
                  setEditing(null);
                }}
              />
            </div>
          ) : null}
        </section>

        <section className="card" data-testid="intake-term">
          <div className="card-h">
            <h2>Term</h2>
          </div>
          <div className="termrow">
            <label className="field">
              <span>Contract term</span>
              <select
                className="input"
                data-testid="intake-term-select"
                value={review.term == null ? "" : String(review.term)}
                onChange={(e) => set({ term: e.target.value === "" ? null : e.target.value === "month_to_month" ? "month_to_month" : (Number(e.target.value) as ContractTerm) })}
              >
                {review.term == null ? <option value="">Pick a term</option> : null}
                {TERMS.map((t) => (
                  <option key={t} value={String(t)}>
                    {formatTerm(t)}
                  </option>
                ))}
              </select>
            </label>
            <p className="said">
              {x.term.text ? (
                <>
                  On the quote: <q>{x.term.text}</q>
                </>
              ) : (
                "The quote doesn\u2019t say."
              )}
            </p>
          </div>
        </section>

        <LinesCard
          intake={intake}
          review={review}
          product={product}
          dealerName={dealerName}
          lit={hl?.index ?? null}
          staff={ops}
          canLink={canLink}
          canOn={canOn}
          recordOf={recordOf}
          records={records}
          checked={sync.data?.catalog ?? null}
          fixingId={fixingId}
          switched={switched}
          onShow={(i) => {
            const printed = x.lines[i];
            const found = printed ? (intake.parts ?? []).find((p) => p.index === printed.part - 1) ?? (intake.parts ?? []).find((p) => p.index === printed.part) : null;
            if (found) setPart(found.index);
            setHl((h) => ({ index: i, seq: (h?.seq ?? 0) + 1 }));
          }}
          onPick={(i) => setPick(i)}
          canAdd={canOn}
          onAdd={(i) => {
            setAddProblem(null);
            setAdding(i);
          }}
          quotable={isQuotable}
          blockersOf={blockersOf}
          onChoice={setLine}
          onLink={(id) => setObFor(id)}
          onSwitch={(i, id) => void switchOn(i, id)}
        />

        {low.length ? (
          <section className="card ik-approval" data-testid="intake-approval">
            <div className="card-h">
              <h2>Needs approval</h2>
              <Chip kind="warn" icon="triangle-alert">
                Below the floor
              </Chip>
            </div>
            <p>{low.length === 1 ? "One line is" : `${low.length} lines are`} below the floor, so Phoneware approves this before it goes to the customer.</p>
            <ul className="ik-low">
              {low.map((l) => (
                <li key={l.index}>
                  {l.product?.name ?? l.productId} at {formatCents(l.unitPrice ?? 0)}, floor {formatCents(l.product?.floorPrice ?? 0)}
                </li>
              ))}
            </ul>
            <label className="field">
              <span>Why this price?</span>
              <textarea
                className="input"
                data-testid="intake-approval-note"
                placeholder="What Phoneware should know, like the competing offer or the size of the deal."
                value={review.note}
                onChange={(e) => set({ note: e.target.value })}
              />
            </label>
            <p className="card-note">Phoneware sees it in their approvals queue when you confirm.</p>
          </section>
        ) : null}

        <TotalsCard intake={intake} review={review} catalog={products} quotable={isQuotable} />

        {problem ? (
          <div className="callout stop" role="alert" data-testid="intake-confirm-error">
            <Icon name="triangle-alert" size={15} />
            <div className="cbody">
              <b>Nothing was created.</b>
              {problem}
            </div>
          </div>
        ) : null}
        {writes ? (
          <div className="confirm-bar" data-testid="intake-confirm-bar">
            <span className={g.ok ? "left ready" : "left block"} data-testid="intake-gate">
              <Icon name={g.ok ? "circle-check" : "circle-alert"} size={15} />
              <span>{g.msg}</span>
            </span>
            <DiscardButton onClick={onDiscard} />
            {confirmButton("intake-confirm")}
          </div>
        ) : null}
      </Split>

      {pick != null ? (
        <PickSheet
          line={x.lines[pick]}
          match={match.lines.find((m) => m.index === pick) ?? null}
          catalog={products}
          records={ops ? (records ?? []) : null}
          dealerName={dealerName}
          onClose={() => setPick(null)}
          onPick={(pid) => {
            setLine(pick, { pick: pid, out: false });
            setPick(null);
          }}
        />
      ) : null}
      {adding != null && x.lines[adding] ? (
        <AddSheet
          from={null}
          start={addStart(x.lines[adding], match.lines.find((m) => m.index === adding)?.unitPrice ?? null)}
          list={records ?? []}
          checked={sync.data?.catalog ?? null}
          busy={addBusy}
          problem={addProblem}
          onClose={() => {
            setAdding(null);
            setAddProblem(null);
          }}
          create={(input) => createFor(adding, input)}
        />
      ) : null}
      {obFor && obProduct ? (
        <Sheet title="Link to OneBill" onClose={() => setObFor(null)} foot={<button className="btn" type="button" onClick={() => setObFor(null)}>Cancel</button>}>
          <div className="omni-cat" data-testid="ob-sheet">
            {fixError ? <Callout kind="stop" icon="triangle-alert">{fixError}</Callout> : null}
            <div className="obhead">
              <b>{obProduct.name}</b>
              <span>{`${atRef(obProduct)} \u00b7 Billed ${billedText(obProduct).toLowerCase()} \u00b7 List ${formatCents(obProduct.listPrice)}`}</span>
            </div>
            <p className="obnote" data-testid="ob-note">
              {obLacks ? `OneBill has no product with code ${obLacks}, so a person confirms` : "A person confirms"} which OneBill product and plan this bills on. Until then dealers can&rsquo;t quote it.
            </p>
            <OneBillPicker
              p={obProduct}
              where="intake"
              busy={fixingId != null}
              products={records ?? []}
              onLink={(code, plan) => void linkProduct(obProduct.id, code, plan)}
            />
          </div>
        </Sheet>
      ) : null}
      {custSheet ? (
        <CustomerSheet
          customers={(customers.data ?? []).filter((c) => c.dealerId === intake.dealerId)}
          loading={!customers.data}
          dealerName={dealerName}
          onClose={() => setCustSheet(false)}
          onPick={(cid) => {
            set({ customer: { kind: "existing", id: cid, why: "picked" } });
            setCustSheet(false);
          }}
        />
      ) : null}
    </>
  );
}

function ContactForm({ draft, onChange, saveLabel, onCancel, onSave }: { draft: ContactEdit; onChange: (next: ContactEdit) => void; saveLabel: string; onCancel: () => void; onSave: () => void }): React.ReactElement {
  const field = (key: keyof ContactEdit, label: string, wide = false) => (
    <label className={wide ? "field c6" : "field c3"}>
      <span>{label}</span>
      <input className="input" data-testid={`ct-${key}`} value={draft[key]} onChange={(e) => onChange({ ...draft, [key]: e.target.value })} />
    </label>
  );
  return (
    <div className="ctform" data-testid="ct-form">
      {field("firstName", "First name")}
      {field("lastName", "Last name")}
      {field("email", "Email", true)}
      {field("phone", "Phone")}
      {field("title", "Title")}
      <div className="btnrow">
        <button className="btn small" type="button" onClick={onCancel}>Cancel</button>
        <button className="btn small primary" type="button" data-testid="ct-save" onClick={onSave}><Icon name="check" size={13} />{saveLabel}</button>
      </div>
    </div>
  );
}

function ExistingCustomerCard({
  customer,
  why,
  printed,
  dealerName,
  canMakeNew,
  onChange,
  onNew,
}: {
  customer: CustomerSummary | null;
  why: "phone" | "name" | "company_key" | "autotask_id" | "picked" | "candidate";
  printed: { name: string | null; phone: string | null };
  dealerName: string;
  canMakeNew: boolean;
  onChange: () => void;
  onNew: () => void;
}): React.ReactElement {
  const pdfSays = [printed.name, printed.phone].filter(Boolean).join(", ");
  const mine = why === "picked" || why === "candidate";
  const reason: Record<typeof why, React.ReactNode> = {
    phone: (
      <>
        <b>Matched by phone.</b> {printed.phone} on the quote is this customer&rsquo;s main phone.
      </>
    ),
    autotask_id: (
      <>
        <b>The same Autotask company.</b> The KQM quote names this company.
      </>
    ),
    name: (
      <>
        <b>Matched by name.</b> The name on the quote is this customer&rsquo;s name.
      </>
    ),
    company_key: (
      <>
        <b>Matched by company name.</b> {printed.name} on the quote is this customer&rsquo;s name, written another way.
      </>
    ),
    picked: (
      <>
        <b>You picked this customer.</b> {pdfSays ? `The quote says ${pdfSays}.` : ""}
      </>
    ),
    candidate: (
      <>
        <b>You picked this customer</b> from the possible matches. {pdfSays ? `The quote says ${pdfSays}.` : ""}
      </>
    ),
  };
  const place = [customer?.address ? `${customer.address.city}, ${customer.address.state}` : null, customer?.phone].filter(Boolean).join(" \u00b7 ");
  return (
    <section className="card" data-testid="intake-customer">
      <div className="card-h">
        <h2>Customer</h2>
        <Chip kind="ok" icon="check">
          {dealerName} customer
        </Chip>
      </div>
      <div className="cust-hit">
        <span className="hit-av">
          <Icon name="building-2" size={18} />
        </span>
        <div className="who">
          <b data-testid="intake-customer-name">{customer?.name ?? "Loading the customer."}</b>
          {place ? <span>{place}</span> : null}
        </div>
        <button className="btn small" type="button" data-testid="intake-customer-change" onClick={onChange}>
          Use a different customer
        </button>
      </div>
      <div className={mine ? "ik-reason picked" : "ik-reason"} data-testid="intake-customer-reason">
        <Icon name={mine ? "user" : "circle-check"} size={15} />
        <span>{reason[why]}</span>
      </div>
      {canMakeNew ? (
        <div className="btnrow ik-after">
          <button className="link" type="button" data-testid="intake-customer-new" onClick={onNew}>
            Make it a new prospect instead
          </button>
        </div>
      ) : null}
    </section>
  );
}

function NewCustomerCard({
  review,
  candidates,
  printedName,
  summaryOf,
  dealerName,
  onForm,
  onUse,
  onPick,
}: {
  review: Review;
  candidates: Array<{ autotaskCompanyId: number; name: string; score: number }>;
  printedName: string;
  summaryOf: (id: number) => CustomerSummary | null;
  dealerName: string;
  onForm: (form: Review["form"]) => void;
  onUse: (id: number) => void;
  onPick: () => void;
}): React.ReactElement {
  const f = review.form;
  const field = (k: keyof Review["form"], label: string, cls: string, autoComplete: string, required: boolean) => (
    <label className={`field ${cls}`}>
      <span>{label}</span>
      <input
        className={required && !f[k].trim() ? "input bad" : "input"}
        autoComplete={autoComplete}
        data-testid={`intake-new-${k}`}
        value={f[k]}
        onChange={(e) => onForm({ ...f, [k]: e.target.value })}
      />
    </label>
  );
  return (
    <section className="card" data-testid="intake-customer-new-card">
      <div className="card-h">
        <h2>Customer</h2>
        <span className="chip azure">
          <Icon name="user-plus" size={10} />
          New prospect
        </span>
      </div>
      <p className="small ik-text">
        No {dealerName} customer has this phone or this name. When you confirm, Omni adds it to Autotask as a Prospect under {dealerName}, with a PW account number.
      </p>
      {candidates.length ? (
        <>
          <p className="ik-lbl">Could it be one of these?</p>
          <ul className="ik-cands">
            {candidates.map((cand) => {
              const s = summaryOf(cand.autotaskCompanyId);
              return (
                <li key={cand.autotaskCompanyId}>
                  <span className="cav">
                    <Icon name="building-2" size={15} />
                  </span>
                  <div>
                    <b>{cand.name}</b>
                    <span>{[sharedWords(printedName, cand.name), s?.address ? `${s.address.city}, ${s.address.state}` : null, s?.phone].filter(Boolean).join(" \u00b7 ")}</span>
                  </div>
                  <button className="btn small" type="button" data-testid={`intake-candidate-${cand.autotaskCompanyId}`} onClick={() => onUse(cand.autotaskCompanyId)}>
                    Use this customer
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}
      <p className="ik-lbl">The new prospect, filled in from the quote</p>
      <div className="ik-form">
        {field("name", "Company name", "f4", "organization", true)}
        {field("phone", "Main phone", "f2", "tel", true)}
        {field("line1", "Street", "f4", "address-line1", true)}
        {field("line2", "Suite or unit", "f2", "address-line2", false)}
        {field("city", "City", "f3", "address-level2", true)}
        {field("state", "State", "f1", "address-level1", true)}
        {field("postalCode", "ZIP", "f2", "postal-code", true)}
      </div>
      <div className="btnrow ik-after">
        <button className="link" type="button" data-testid="intake-customer-pick" onClick={onPick}>
          Pick an existing customer instead
        </button>
      </div>
    </section>
  );
}

const WHY_MATCHED: Record<"code" | "name" | "product_key", string> = { code: "By code", name: "By name", product_key: "By name, with the words in another order" };

function LinesCard({
  intake,
  review,
  product,
  dealerName,
  lit,
  staff,
  canLink,
  canOn,
  recordOf,
  records,
  checked,
  fixingId,
  switched,
  onShow,
  onPick,
  canAdd,
  onAdd,
  quotable,
  blockersOf,
  onChoice,
  onLink,
  onSwitch,
}: {
  intake: QuoteIntakeView;
  review: Review;
  product: (id: string | null) => PricedProduct | null;
  dealerName: string;
  lit: number | null;
  staff: boolean;
  canLink: boolean;
  canOn: boolean;
  recordOf: (id: string) => CatalogProductV2 | null;
  /** Staff: the whole catalog. Null for a dealer. */
  records: readonly CatalogProductV2[] | null;
  /** Staff: what the last catalog check read, for OneBill's side of a code. */
  checked: CatalogSyncState | null;
  fixingId: string | null;
  switched: ReadonlySet<string>;
  onShow: (i: number) => void;
  onPick: (i: number) => void;
  /** catalog.manage: a Super admin can create a product for a line Omni couldn't match. */
  canAdd: boolean;
  onAdd: (i: number) => void;
  quotable: (id: string) => boolean;
  /** Staff: why the dealer can't quote a product, from its catalog record. */
  blockersOf: (id: string) => QuotableBlocker[];
  onChoice: (i: number, choice: { pick: string | null; out: boolean }) => void;
  onLink: (productId: string) => void;
  onSwitch: (i: number, productId: string) => void;
}): React.ReactElement {
  const printed = intake.extraction?.result.lines ?? [];
  const matches = intake.match?.lines ?? [];
  const states = matches.map((m) => lineState(m, review.lines[m.index], quotable).kind);
  const count = (k: string) => states.filter((s) => s === k).length;
  const words: CodeWordsContext = { staff, dealerName, records };
  const groups: Array<[IntakeLine["billing"], string]> = [
    ["monthly", "Monthly on the quote"],
    ["one_time", "One-time on the quote"],
    ["unknown", "Billing not given"],
  ];
  return (
    <>
    {matches.some((m) => m.codeMatch) ? (
      <div className="ik-rule" data-testid="intake-match-rule">
        <Callout icon="info">
          <b>Lines match by product code first.</b> A line&rsquo;s code is compared with each product&rsquo;s own code, then with the OneBill code it bills on. Only when no product has the code does Omni try the name. A printed &quot;-PHNW&quot; or &quot;-Retail&quot; is ignored.
        </Callout>
      </div>
    ) : null}
    <section className="card" data-testid="intake-lines">
      <div className="card-h">
        <h2>Lines</h2>
        <div className="lsum">
          {count("matched") ? <Chip kind="ok">{count("matched")} matched</Chip> : null}
          {count("picked") ? <span className="chip azure">{count("picked")} picked</span> : null}
          {count("open") ? <Chip kind="warn">{count("open")} to pick or leave out</Chip> : null}
          {count("out") ? <Chip kind="quiet">{count("out")} left out</Chip> : null}
        </div>
      </div>
      {groups.map(([billing, title]) => {
        const idx = matches.filter((m) => printed[m.index]?.billing === billing);
        if (!idx.length) return null;
        const pagesOf = [...new Set(idx.map((m) => printed[m.index].page))];
        return (
          <React.Fragment key={billing}>
            <div className="lgroup">
              <h3>{title}</h3>
              <span>
                {pagesOf.length === 1 ? "Page" : "Pages"} {pagesOf.join(" and ")}
              </span>
            </div>
            {idx.map((m) => {
              // The product the row shows: the one picked for the line, else the line's own.
              const shown = review.lines[m.index]?.pick ?? m.product?.productId ?? null;
              return (
              <LineRow
                key={m.index}
                m={m}
                line={printed[m.index]}
                part={(intake.parts ?? []).find((p) => p.index === printed[m.index].part - 1) ?? (intake.parts ?? []).find((p) => p.index === printed[m.index].part) ?? null}
                choice={review.lines[m.index]}
                product={product}
                dealerName={dealerName}
                lit={lit === m.index}
                staff={staff}
                canLink={canLink}
                canOn={canOn}
                words={words}
                checked={checked}
                record={shown ? recordOf(shown) : null}
                fixing={Boolean(shown && fixingId === shown)}
                switchedOn={Boolean(shown && switched.has(shown))}
                onShow={() => onShow(m.index)}
                onPick={() => onPick(m.index)}
                canAdd={canAdd}
                onAdd={() => onAdd(m.index)}
                quotable={quotable}
                ownBlockers={shown ? blockersOf(shown) : []}
                onChoice={(c) => onChoice(m.index, c)}
                onLink={() => shown && onLink(shown)}
                onSwitch={() => shown && onSwitch(m.index, shown)}
              />
              );
            })}
          </React.Fragment>
        );
      })}
      <p className="card-note">Each line keeps the quantity and price on the quote. Change them in the draft after you confirm.</p>
    </section>
    </>
  );
}

function LineRow({
  m,
  line,
  part,
  choice,
  product,
  dealerName,
  lit,
  staff,
  canLink,
  canOn,
  record,
  words,
  checked,
  fixing,
  switchedOn,
  onShow,
  onPick,
  canAdd,
  onAdd,
  quotable,
  ownBlockers,
  onChoice,
  onLink,
  onSwitch,
}: {
  m: QuoteIntakeLineView;
  line: IntakeLine;
  part: Omit<IntakePart, "gcsPath"> | null;
  choice: { pick: string | null; out: boolean } | undefined;
  product: (id: string | null) => PricedProduct | null;
  dealerName: string;
  lit: boolean;
  staff: boolean;
  canLink: boolean;
  canOn: boolean;
  record: CatalogProductV2 | null;
  words: CodeWordsContext;
  checked: CatalogSyncState | null;
  fixing: boolean;
  switchedOn: boolean;
  onShow: () => void;
  onPick: () => void;
  canAdd: boolean;
  onAdd: () => void;
  quotable: (id: string) => boolean;
  /** Why the dealer can't quote the row's product, from its catalog record, for one the review has no reasons for. */
  ownBlockers: QuotableBlocker[];
  onChoice: (c: { pick: string | null; out: boolean }) => void;
  onLink: () => void;
  onSwitch: () => void;
}): React.ReactElement {
  const s = lineState(m, choice, quotable);
  const billing = printedBilling(line);
  const printedAmount = [line.qty && line.unitPrice ? `${line.qty} \u00d7 ${line.unitPrice}` : (line.qty ?? line.unitPrice), line.extended].filter(Boolean).join(" = ");
  const amount = printedAmount ? printedAmount + (billing === "monthly" ? " a month" : billing === "one_time" ? " one-time" : "") : "";
  const pdfPart = !part || part.kind === "pdf";
  const chip = pdfPart ? `p${line.page}` : part.label;
  const left = (
    <div className="ln-pdf">
      <div className={pdfPart ? "src" : "src lab"}>
        <button className={pdfPart ? "pgchip" : "pgchip lab"} type="button" title={pdfPart ? `Show it on page ${line.page}` : `Show it in ${part.label}`} aria-label={pdfPart ? `${chip}: show this line on page ${line.page}` : `Show this line in ${part.label}`} data-testid={`intake-line-page-${m.index}`} onClick={onShow}>
          {chip}
        </button>
        <span className="txt">
          {lineText(line)}
          {line.code ? <span className="code">{line.code}</span> : null}
        </span>
      </div>
      {amount ? <span className="amt">{amount}</span> : null}
    </div>
  );
  let right: React.ReactNode;
  if (s.kind === "out") {
    right = (
      <>
        <div className="prod">
          <Icon name="minus" size={15} />
          <b className="out">{lineText(line)}</b>
        </div>
        <div className="flags">
          <span className="flag out">Left out. It won&rsquo;t be on the draft.</span>
        </div>
        <div className="btnrow">
          <button className="btn small" type="button" data-testid={`intake-line-undo-${m.index}`} onClick={() => onChoice({ pick: null, out: false })}>
            <Icon name="undo-2" size={13} />
            Undo
          </button>
        </div>
      </>
    );
  } else if (s.kind === "open") {
    const w = openWords(m, line.code ?? null, words);
    // A dealer is offered what it can quote. Staff are offered switched-off products too, as the
    // catalog has them, so a code two switched-off products share can still be settled here.
    const suggestions = m.suggestions.flatMap((ref) => {
      const priced = product(ref.productId);
      if (!staff) return priced ? [{ id: priced.id, name: priced.name, detail: `${codeLine(priced)} \u00b7 list ${formatCents(priced.listPrice)}` }] : [];
      const rec = words.records?.find((r) => r.id === ref.productId) ?? null;
      return [{ id: ref.productId, name: ref.name, detail: [ref.code, rec ? atRef(rec) : null, `list ${formatCents(ref.listPrice)}`].filter(Boolean).join(" \u00b7 ") }];
    });
    const onebill = staff && m.codeMatch?.outcome === "none" && checked ? onebillWithCode(m.codeMatch.code, checked.notInOmni.onebill) : null;
    right = (
      <>
        <div className="prod">
          <Icon name="triangle-alert" size={15} />
          <b className="miss">{w.title}</b>
        </div>
        <div className="why" data-testid={`intake-line-why-${m.index}`}>{w.why}</div>
        {onebill ? (
          <p className="pc-aside" data-testid={`intake-line-onebill-${m.index}`}>
            <Icon name="info" size={13} />
            <span>
              OneBill has {onebill.productCode}
              {onebill.plan ? `, plan ${onebill.plan.id}, ${planText(onebill.plan)}` : ""}.
            </span>
          </p>
        ) : null}
        <div className="sugg">
          {suggestions.length ? (
            <>
              <p className="ik-lbl">{w.label}</p>
              {suggestions.map((p) => (
                <button key={p.id} className="sg" type="button" data-testid={`intake-line-use-${m.index}-${p.id}`} onClick={() => onChoice({ pick: p.id, out: false })}>
                  <span>
                    <b>
                      {p.name}
                      {w.sameCode.has(p.id) ? (
                        <Chip kind="ok" testId={`intake-line-same-${m.index}-${p.id}`}>
                          Same code
                        </Chip>
                      ) : null}
                    </b>
                    <small>{p.detail}</small>
                  </span>
                  <span className="use">Use it</span>
                </button>
              ))}
            </>
          ) : (
            <p className="none">Nothing in {w.where}&rsquo;s catalog is close.</p>
          )}
        </div>
        <div className="btnrow">
          <button className="btn small" type="button" data-testid={`intake-line-pick-${m.index}`} onClick={onPick}>
            <Icon name="search" size={13} />
            Pick a product
          </button>
          <button className="btn small" type="button" data-testid={`intake-line-out-${m.index}`} onClick={() => onChoice({ pick: null, out: true })}>
            <Icon name="minus" size={13} />
            Leave out
          </button>
          {canAdd ? (
            <button className="btn small" type="button" data-testid={`intake-line-create-${m.index}`} onClick={onAdd}>
              <Icon name="plus" size={13} />
              Create product
            </button>
          ) : null}
        </div>
      </>
    );
  } else if (s.kind === "switched_off" || s.kind === "fixing") {
    // Fixing: the switched-off product picked for the line, the line's own or a suggestion, or
    // one picked from the whole catalog or created here, whose reasons come from its record.
    const ref = s.kind === "fixing" && m.product?.productId !== s.productId ? (m.suggestions.find((x) => x.productId === s.productId) ?? null) : m.product;
    const blockers = ref?.blockers ?? ownBlockers;
    const fixes = inlineFixes(blockers);
    const live = fixes.enable && blockers.length === 1;
    // What Switch on still waits for: the OneBill link fixed here, and any other reason, such as a product Autotask refused.
    const waits = fixes.link
      ? fixes.elsewhere.length
        ? "Switch on works once it\u2019s linked to OneBill and the rest is fixed."
        : "Switch on works once it\u2019s linked to OneBill."
      : "Switch on works once the rest is fixed.";
    const name = ref?.name ?? record?.name ?? "A product this dealer can\u2019t quote now";
    const codeWhy = matchedWhy(m, words);
    const lacks = ref ? onebillLacksCode(ref.productId, checked?.codes) : null;
    right = (
      <>
        <div className="prod">
          <Icon name="corner-down-right" size={15} />
          <b>
            {name} <span className="chip off" data-testid={`off-chip-${m.index}`}>Switched off</span>
          </b>
        </div>
        <div className="why" data-testid={`intake-line-why-${m.index}`}>
          {s.kind === "fixing" ? <span className="chip azure">You picked it</span> : <span>{codeWhy ?? <>{dealerName} doesn&rsquo;t see it while it&rsquo;s switched off.</>}</span>}
        </div>
        {staff && s.kind === "switched_off" ? <BillsOn record={record} testId={`intake-line-bills-${m.index}`} /> : null}
        <div className="blk" data-testid={`blockers-${m.index}`}>
          <h4>Why {dealerName} can&rsquo;t quote it</h4>
          <ul>
            {blockers.map((blocker) => (
              <li key={blocker.code} data-code={blocker.code}>
                <Icon name="triangle-alert" size={13} />
                <span>{blocker.code === "missing_onebill_link" && lacks ? `OneBill has no product with code ${lacks}. Link it to one, or it can\u2019t be quoted.` : blocker.reason}</span>
              </li>
            ))}
          </ul>
          <div className="fixes">
            {fixes.link && canLink ? (
              <button className="btn small" type="button" data-testid="fix-link" disabled={fixing} onClick={onLink}>
                <Icon name="link" size={13} />Link to OneBill
              </button>
            ) : null}
            {fixes.enable && canOn ? (
              <button className={live ? "btn small primary" : "btn small"} type="button" data-testid="fix-switch" disabled={!live || fixing} title={live ? undefined : waits} onClick={onSwitch}>
                <Icon name="power" size={13} />
                {fixing ? "Switching on" : "Switch on"}
              </button>
            ) : null}
          </div>
          {fixes.enable && canOn && !live ? <p className="fixnote" data-testid={`fix-waits-${m.index}`}>{waits}</p> : null}
          {fixes.link && !canLink && staff ? <p className="fixnote">Billing ops or a Super admin links it to OneBill.</p> : null}
          {fixes.enable && !canOn && staff ? <p className="fixnote">A Super admin switches it on.</p> : null}
          {switchedOn ? null : null}
        </div>
        <div className="btnrow">
          <button className="btn small" type="button" data-testid={`intake-line-pick-${m.index}`} onClick={onPick}>
            <Icon name="search" size={13} />Pick another product
          </button>
          <button className="btn small" type="button" data-testid={`intake-line-out-${m.index}`} onClick={() => onChoice({ pick: null, out: true })}>
            <Icon name="minus" size={13} />Leave out
          </button>
        </div>
      </>
    );
  } else {
    const p = product(s.productId);
    const price = m.unitPrice ?? p?.listPrice ?? null;
    const low = p?.floorPrice != null && price != null && price < p.floorPrice;
    const differs = billingDiffers(m, line, s, p);
    right = (
      <>
        <div className="prod">
          <Icon name="corner-down-right" size={15} />
          <b>{p?.name ?? "A product this dealer can\u2019t quote now"}</b>
        </div>
        <div className="why">
          {s.kind === "picked" ? (
            <>
              <span className="chip azure">You picked it</span>
              <button className="link" type="button" data-testid={`intake-line-change-${m.index}`} onClick={onPick}>
                Change
              </button>
              <button className="link" type="button" data-testid={`intake-line-undo-${m.index}`} onClick={() => onChoice({ pick: null, out: false })}>
                Undo
              </button>
            </>
          ) : (
            <span data-testid={`intake-line-why-${m.index}`}>{matchedWhy(m, words) ?? legacyWhy(m, line, p)}</span>
          )}
        </div>
        <div className="px">
          {line.unitPrice && m.unitPrice != null ? <span className="pdfp">Quote {formatCents(m.unitPrice)}</span> : <span className="pdfp">No price on the quote</span>}
          {p ? (
            <span className="listp">
              List {formatCents(p.listPrice)}
              {p.floorPrice != null ? ` \u00b7 floor ${formatCents(p.floorPrice)}` : ""}
            </span>
          ) : null}
        </div>
        {low || differs || m.qty == null ? (
          <div className="flags">
            {low && p?.floorPrice != null ? (
              <span className="flag low" data-testid={`intake-line-low-${m.index}`}>
                <Icon name="triangle-alert" size={13} />
                Below the floor of {formatCents(p.floorPrice)}. Needs approval.
              </span>
            ) : null}
            {differs && p ? (
              <span className="flag bill" data-testid={`intake-line-billing-${m.index}`}>
                <Icon name="info" size={13} />
                Billing differs: {billWord(billing)} on the quote, {billWord(p.billing)} in Omni. It goes on the draft as {billWord(p.billing)}.
              </span>
            ) : null}
            {m.qty == null ? (
              <span className="flag low">
                <Icon name="triangle-alert" size={13} />
                No quantity on the quote.
              </span>
            ) : null}
          </div>
        ) : null}
        {staff && s.kind === "matched" ? <BillsOn record={record} testId={`intake-line-bills-${m.index}`} /> : null}
      </>
    );
  }
  const cls = ["ln", s.kind === "open" ? "open" : "", s.kind === "out" ? "isout" : "", s.kind === "switched_off" || s.kind === "fixing" ? "off" : "", lit ? "hl" : ""].filter(Boolean).join(" ");
  return (
    <div className={cls} data-testid={`intake-line-${m.index}`} data-state={s.kind} data-product={"productId" in s ? s.productId : undefined}>
      {left}
      <div className="ln-omni">{right}</div>
    </div>
  );
}

/** Why a line with no code match matched: no printed code, or an intake read before codes (#2057). */
function legacyWhy(m: QuoteIntakeLineView, line: IntakeLine, p: PricedProduct | null): string {
  if (m.why !== "code") return m.why ? WHY_MATCHED[m.why] : "";
  const ownCode = Boolean(line.code && p && p.code.toUpperCase() === line.code.toUpperCase());
  return `By code ${line.code ?? ""}${ownCode ? "" : ", its OneBill code"}`;
}

/** What Create product starts from: the line's name and code without a printed "-PHNW" or "-Retail", how it bills (one-time unless it says monthly), and its price. */
function addStart(line: IntakeLine, unitPrice: number | null): AddStart {
  return {
    name: withoutSuffix(lineText(line)),
    code: line.code ? withoutSuffix(line.code) : "",
    billing: printedBilling(line) === "monthly" ? "monthly" : "one_time",
    listPrice: unitPrice != null ? (unitPrice / 100).toFixed(2) : "",
  };
}

/** Staff: the OneBill product and plan a linked product bills on. */
function BillsOn({ record, testId }: { record: CatalogProductV2 | null; testId: string }): React.ReactElement | null {
  if (!record?.onebill || record.billingLink.state !== "linked") return null;
  return (
    <div className="flags pc-flags">
      <span className="pc-bills" data-testid={testId}>
        <Icon name="link" size={13} />
        Bills on <b>{record.onebill.productCode}</b> &middot; plan {record.onebill.pricePlanId}
      </span>
    </div>
  );
}

function TotalsCard({ intake, review, catalog, quotable }: { intake: QuoteIntakeView; review: Review; catalog: PricedProduct[]; quotable?: (id: string) => boolean }): React.ReactElement {
  const t = totalsCheck(intake, review, catalog, quotable);
  const printed = t.rows.some((r) => r.printed != null);
  const differs = t.rows.some((r) => r.printed != null && r.printed !== r.draft);
  if (!printed) {
    return (
      <section className="card" data-testid="intake-totals">
        <div className="card-h">
          <h2>Totals on the draft</h2>
          <span className="note">The quote gives no totals to check against</span>
        </div>
        <table className="tc">
          <thead>
            <tr>
              <th>Billed</th>
              <th>On the draft</th>
            </tr>
          </thead>
          <tbody>
            {t.rows.map((r) => (
              <tr key={r.label}>
                <td>{r.label}</td>
                <td>{formatCents(r.draft)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {intake.source.kind === "kqm" ? <p className="card-note">KQM&rsquo;s API gives no totals, so compare the draft with the quote in KQM.</p> : null}
      </section>
    );
  }
  return (
    <section className="card" data-testid="intake-totals">
      <div className="card-h">
        <h2>Totals check</h2>
        <span className="note">The quote&rsquo;s totals against the lines going on the draft</span>
      </div>
      <table className="tc">
        <thead>
          <tr>
            <th>Billed</th>
            <th>On the quote</th>
            <th>On the draft</th>
            <th>Check</th>
          </tr>
        </thead>
        <tbody>
          {t.rows.map((r) => (
            <tr key={r.label} data-testid={`intake-total-${r.label === "Monthly" ? "monthly" : "one-time"}`}>
              <td>{r.label}</td>
              <td>{r.printed == null ? "" : formatCents(r.printed)}</td>
              <td>{formatCents(r.draft)}</td>
              <td className="res">
                {r.printed == null ? (
                  <Chip kind="quiet">No total printed</Chip>
                ) : r.printed === r.draft ? (
                  <Chip kind="ok" icon="check">
                    Agrees
                  </Chip>
                ) : (
                  <Chip kind="warn">Differs by {formatCents(Math.abs(r.draft - r.printed))}</Chip>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {t.why.length ? (
        <ul className="tc-why">
          {t.why.map((w, i) => (
            <li key={i}>
              <Icon name={w.icon} size={13} />
              <span>{w.text}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {differs ? <p className="card-note">Omni shows a difference. It never changes a price to make the totals agree.</p> : null}
    </section>
  );
}

function PickSheet({
  line,
  match,
  catalog,
  records,
  dealerName,
  onClose,
  onPick,
}: {
  line: IntakeLine;
  match: QuoteIntakeLineView | null;
  catalog: PricedProduct[];
  records: CatalogProductV2[] | null;
  dealerName: string;
  onClose: () => void;
  onPick: (productId: string) => void;
}): React.ReactElement {
  const [find, setFind] = useState("");
  const needle = find.trim().toLowerCase();
  const staffRows = (records ?? []).filter((row) => !needle || `${row.name} ${row.code} ${row.category}`.toLowerCase().includes(needle));
  const quotable = catalog.filter((p) => p.quotable);
  const suggested = (match?.suggestions ?? []).map((sg) => quotable.find((p) => p.id === sg.productId)).filter((p): p is PricedProduct => Boolean(p));
  const dealerRest = quotable.filter((p) => !suggested.includes(p) && (!needle || `${p.name} ${p.code} ${p.category}`.toLowerCase().includes(needle)));
  const item = (p: PricedProduct) => (
    <li key={p.id}>
      <button type="button" data-testid={`intake-pick-${p.id}`} onClick={() => onPick(p.id)}>
        <span>
          <b>{p.name}</b>
          <small>{codeLine(p)}</small>
        </span>
        <span className="amt">{formatCents(p.listPrice)}</span>
      </button>
    </li>
  );
  const billing = printedBilling(line);
  return (
    <Sheet
      title="Pick a product"
      onClose={onClose}
      foot={
        <button className="btn" type="button" onClick={onClose}>
          Cancel
        </button>
      }
    >
      <div className="pk-src" data-testid="intake-pick-sheet">
        {lineText(line)}
        {line.code ? <span className="code"> {line.code}</span> : null}
        <small>
          Page {line.page} of the quote{line.qty || line.unitPrice ? ` \u00b7 ${[line.qty, line.unitPrice].filter(Boolean).join(" \u00d7 ")}` : ""}
          {billing !== "unknown" ? ` ${billWord(billing)}` : ""}
        </small>
      </div>
      {suggested.length ? (
        <>
          <p className="ik-lbl">Closest in {dealerName}&rsquo;s catalog</p>
          <ul className="pk-list">{suggested.map(item)}</ul>
        </>
      ) : null}
      <p className="ik-lbl">{dealerName}&rsquo;s catalog</p>
      <label className="search pk-find">
        <Icon name="search" size={15} />
        <span className="sr">Find a product</span>
        <input className="input" type="search" placeholder="Product name or code" data-testid="intake-pick-search" value={find} onChange={(e) => setFind(e.target.value)} />
      </label>
      <ul className="pk-list" data-testid="intake-pick-list">
        {records ? (
          staffRows.length ? staffRows.map((row) => (
            <li key={row.id}>
              <button type="button" data-testid={`intake-pick-${row.id}`} onClick={() => onPick(row.id)}>
                <span>
                  <b>
                    {row.name}
                    {row.enabled && row.billingLink?.state === "linked" ? null : <span className="chip off">Switched off</span>}
                  </b>
                  <small>{row.category}</small>
                </span>
                <span className="amt">{formatCents(row.listPrice)}</span>
              </button>
            </li>
          )) : <li className="empty">Nothing in Phoneware&rsquo;s catalog matches &ldquo;{find}&rdquo;.</li>
        ) : dealerRest.length ? dealerRest.map(item) : <li className="empty">Nothing in {dealerName}&rsquo;s catalog matches &ldquo;{find}&rdquo;.</li>}
      </ul>
      <p className="card-note">
        {line.unitPrice
          ? `The line keeps the quote\u2019s price, ${line.unitPrice}. Change it in the draft if it should be different.`
          : "There\u2019s no price on the quote for it, so it goes on the draft at the product\u2019s list price. Change it in the draft if it should be different."}
      </p>
    </Sheet>
  );
}

function CustomerSheet({
  customers,
  loading,
  dealerName,
  onClose,
  onPick,
}: {
  customers: CustomerSummary[];
  loading: boolean;
  dealerName: string;
  onClose: () => void;
  onPick: (id: number) => void;
}): React.ReactElement {
  const [find, setFind] = useState("");
  const needle = find.trim().toLowerCase();
  const list = customers.filter((c) => !needle || `${c.name} ${c.phone ?? ""}`.toLowerCase().includes(needle)).sort((a, b) => a.name.localeCompare(b.name));
  return (
    <Sheet
      title="Pick the customer"
      onClose={onClose}
      foot={
        <button className="btn" type="button" onClick={onClose}>
          Cancel
        </button>
      }
    >
      <label className="search">
        <Icon name="search" size={15} />
        <span className="sr">Find a customer</span>
        <input className="input" type="search" placeholder="Name or phone" data-testid="intake-customer-search" value={find} onChange={(e) => setFind(e.target.value)} />
      </label>
      <ul className="pk-list" data-testid="intake-customer-list">
        {loading ? (
          <li className="empty">Loading customers.</li>
        ) : list.length ? (
          list.map((c) => (
            <li key={c.autotaskCompanyId}>
              <button type="button" data-testid={`intake-customer-option-${c.autotaskCompanyId}`} onClick={() => onPick(c.autotaskCompanyId)}>
                <span>
                  <b>{c.name}</b>
                  <small>{[c.address ? `${c.address.city}, ${c.address.state}` : null, c.phone].filter(Boolean).join(" \u00b7 ")}</small>
                </span>
              </button>
            </li>
          ))
        ) : (
          <li className="empty">No {dealerName} customer matches &ldquo;{find}&rdquo;.</li>
        )}
      </ul>
      <p className="card-note">{dealerName}&rsquo;s customers in Autotask.</p>
    </Sheet>
  );
}
